import { Button } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { Checkbox } from '@/components/Form/Checkbox';
import { DatePicker } from '@/components/Form/DatePicker';
import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { ActionBar } from '@/components/Layout';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { usePepQuestion } from '@/hooks/usePepQuestion';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectUserData } from '@/redux/user-data';

import { PEPAnswer } from '@/types/api/pep';
import { CountryISO } from '@/types/places';

import { getDataTestId } from '@/utils/browser';
import { isCaymanUser } from '@/utils/user';

import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

export function QuestionAboutRelatives({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t, country: translatedCountry } = useTranslate();
  const { answerYes, answerNo, error, isPending } = usePepQuestion({
    onAnswered,
    question: props.question,
    target: props.pepAnswerTarget,
  });
  const { dateFormat } = useAppSelector(selectConfigsState);
  const { user } = useAppSelector(selectUserData);
  const {
    value: isYesAnswer,
    setTrue: onYes,
    setFalse: resetAnswer,
  } = useBoolean(false);
  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors, isValid },
    reset: resetForm,
    watch,
    setValue,
    trigger,
  } = useForm<Partial<PEPAnswer>>({
    defaultValues: {
      fullName: '',
      relationship: '',
      isPersonWorking: true,
      startDate: '',
      endDate: '',
      ...(isCaymanUser(user) && {
        countryCode: CountryISO.Cayman,
        position: 'Politician',
      }),
    },
    mode: 'onChange',
  });

  const isCurrentWorking = watch('isPersonWorking');
  const endDate = watch('endDate');

  useEffect(() => {
    if (isCurrentWorking) {
      trigger('endDate');

      if (endDate) {
        setValue('endDate', '');
      }
    }
  }, [endDate, isCurrentWorking, setValue, trigger]);

  const onCancel = () => {
    resetAnswer();
    resetForm();
  };

  const onAnswerYes = handleSubmit(payload => {
    answerYes(payload);
  });

  const onAnswerNo = () => {
    answerNo();
  };

  const popularCountryOptions = COUNTRIES_WITH_POPULAR.map(item => ({
    ...item,
    text: translatedCountry[item.value],
  }));

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      hideAdditionalInfo={isYesAnswer}
      onSubmit={onAnswerYes}
    >
      {!isYesAnswer ? (
        <ActionBar>
          <Button
            {...getDataTestId(`basic-info-yes-button`)}
            variant="outlined"
            fullWidth
            onClick={onYes}
          >
            {t('questionDrawer.relative.button.yes')}
          </Button>
          <Button
            {...getDataTestId(`basic-info-no-button`)}
            variant="outlined"
            fullWidth
            onClick={onAnswerNo}
          >
            {t('questionDrawer.relative.button.no')}
          </Button>
        </ActionBar>
      ) : (
        <>
          <TextInput
            type="text"
            control={control}
            name="fullName"
            error={!!visibleErrors.fullName}
            helperText={visibleErrors.fullName?.message}
            rules={{
              required: t('questionDrawer.relative.fullname.required'),
            }}
            label={t('questionDrawer.relative.fullname.label')}
            placeholder={t('questionDrawer.relative.fullname.label')}
            autoComplete="off"
            disabled={isPending}
          />
          <TextInput
            type="text"
            control={control}
            name="relationship"
            error={!!visibleErrors.relationship}
            helperText={visibleErrors.relationship?.message}
            rules={{
              required: t('questionDrawer.relative.relationship.required'),
            }}
            label={t('questionDrawer.relative.relationship.label')}
            placeholder={t('questionDrawer.relative.relationship.placeHolder')}
            autoComplete="off"
            disabled={isPending}
          />
          <TextInput
            type="text"
            control={control}
            name="position"
            error={!!visibleErrors.position}
            helperText={visibleErrors.position?.message}
            rules={{
              required: t('questionDrawer.relative.position.required'),
            }}
            label={t('questionDrawer.relative.position.label')}
            placeholder={t('questionDrawer.relative.position.placeHolder')}
            autoComplete="off"
            disabled={isPending}
          />
          <Select
            options={popularCountryOptions}
            control={control}
            name="countryCode"
            error={!!visibleErrors.countryCode}
            helperText={visibleErrors.countryCode?.message}
            rules={{
              required: t('questionDrawer.relative.countryCode.required'),
            }}
            fullWidth
            placeholder={t('questionDrawer.relative.countryCode.placeHolder')}
            useDrawerOnMobile
            label={t('questionDrawer.relative.countryCode.label')}
            withSearchBar
            disabled={isPending}
          />
          <Checkbox
            control={control}
            name="isPersonWorking"
            label={t('questionDrawer.relative.isPersonWorking.label')}
            disabled={isPending}
          />
          <DatePicker
            name="startDate"
            label={t('questionDrawer.relative.startDate.label')}
            format={dateFormat}
            control={control}
            error={!!visibleErrors.startDate}
            helperText={visibleErrors.startDate?.message}
            rules={{
              required: t('questionDrawer.relative.startDate.required'),
            }}
            disabled={isPending}
          />
          <DatePicker
            name="endDate"
            label={t('questionDrawer.relative.endDate.label')}
            format={dateFormat}
            control={control}
            error={!!visibleErrors.endDate}
            helperText={visibleErrors.endDate?.message}
            rules={{
              validate: {
                required: value =>
                  !isCurrentWorking && !value
                    ? t('questionDrawer.relative.endDate.required')
                    : undefined,
              },
            }}
            disabled={isPending || isCurrentWorking}
          />
          <ActionBar>
            <Button
              {...getDataTestId(`basic-info-cancel-button`)}
              variant="outlined"
              fullWidth
              onClick={onCancel}
            >
              {t('questionDrawer.relative.button.goBack')}
            </Button>
            <Button
              {...getDataTestId(`basic-info-confirm-button`)}
              variant="outlined"
              fullWidth
              type="submit"
              disabled={!isValid}
            >
              {t('questionDrawer.relative.button.proceed')}
            </Button>
          </ActionBar>
        </>
      )}
      <FormErrorLogger formName="QuestionAboutRelatives" control={control} />
    </QuestionForm>
  );
}
